import { ModalNext } from "@ui-kit/Modal/ModalNext";
import { FunctionComponent, useCallback } from "react";
import { TripBuilderTripDuration } from "./TripBuilderTripDuration/TripBuilderTripDuration";
import { TripBuilderUserPreferences } from "./TripBuilderUserPreferences/TripBuilderUserPreferences";
import { TripBuilderGroupComposition } from "./TripBuilderGroupComposition/TripBuilderGroupComposition";
import { TripBuilderLoading } from "./TripBuilderLoading/TripBuilderLoading";
import { TripBuilderResults } from "./TripBuilderResults/TripBuilderResults";
import {
  selectIsOpen,
  selectCurrentStep,
} from "@store/tripBuilder/tripBuilderReducer";
import { useAppSelector } from "@store/store";
import { closeTripBuilder } from "@store/tripBuilder/tripBuilderReducer";
import { useAppDispatch } from "@store/store";
import { Text } from "@ui-kit/Text/Text";
import { useTranslation } from "react-i18next";

import "./TripBuilder.css";
import { useGenerateTripPlanMutation } from "@store/services/backend/tripBuilder";

const stepProgress = {
  tripDuration: 25,
  groupComposition: 50,
  userPreferences: 75,
  loading: 100,
  results: 100,
};

/**
 * Entry point to TripBuilder screens and navigating between them.
 */
const TripBuilder: FunctionComponent = () => {
  const currentStep = useAppSelector(selectCurrentStep);
  const isOpen = useAppSelector(selectIsOpen);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [generateTripPlan, { data, isSuccess }] = useGenerateTripPlanMutation();

  const onClose = useCallback(() => {
    dispatch(closeTripBuilder());
  }, [dispatch]);

  return (
    <ModalNext
      isOpen={isOpen}
      onClose={onClose}
      width={currentStep === "results" ? 862 : 754}
      height={736}
      noPadding>
      <div className="modal-heading">
        <Text variant="fontHeadingMedium">
          {currentStep === "results"
            ? t("screens.tripBuilder.headingResults")
            : t("screens.tripBuilder.heading")}
        </Text>
      </div>
      <div className="progress-bar">
        <div
          className="progress-bar-fill"
          style={{ width: `${stepProgress[currentStep]}%` }}
        />
      </div>
      {currentStep === "tripDuration" && <TripBuilderTripDuration />}
      {currentStep === "groupComposition" && <TripBuilderGroupComposition />}
      {currentStep === "userPreferences" && (
        <TripBuilderUserPreferences generateTripPlan={generateTripPlan} />
      )}
      {currentStep === "loading" && (
        <TripBuilderLoading isSuccess={isSuccess} />
      )}
      {currentStep === "results" && <TripBuilderResults tripPlan={data} />}
    </ModalNext>
  );
};

export { TripBuilder };
